import React, { useState, useEffect } from 'react';
import {
  Card, Table, Tabs, Space, Button, Tag, Modal, Descriptions,
  Statistic, Row, Col, Select, DatePicker, Input, Alert, message,
  Timeline, Badge, Tooltip, Popconfirm, Form, Typography
} from 'antd';
import {
  EyeOutlined, ReloadOutlined, SendOutlined, ClockCircleOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined,
  BarChartOutlined, MessageOutlined, RobotOutlined, CloudSyncOutlined
} from '@ant-design/icons';
import { useI18n } from '../hooks/useI18n';
import { whatsappConnectorService, WhatsAppAccount } from '../services/whatsappConnectorService';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Text } = Typography;

interface MessageRecord {
  id: string;
  whatsapp_message_id: string;
  tenant_code: string;
  whatsapp_account_id: number;
  platform_id: number;
  agent_id: string;
  from_user: string;
  to_user: string;
  message_type: string;
  content: string;
  direction: 'inbound' | 'outbound';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  processing_time_ms?: number;
  error_message?: string;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

interface AsyncTask {
  id: string;
  tenant_code: string;
  whatsapp_account_id: number;
  whatsapp_message_id: string;
  platform_id: number;
  agent_id: string;
  agent_request_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'timeout';
  retry_count: number;
  max_retries: number;
  next_retry_at?: string;
  response?: string;
  created_at: string;
  updated_at: string;
}

interface ProcessingStats {
  totalMessages: number;
  processedMessages: number;
  failedMessages: number;
  pendingAsyncTasks: number;
  avgProcessingTime: number;
  successRate: number;
}

const WhatsAppConnectorMonitorPage: React.FC = () => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<MessageRecord[]>([]);
  const [asyncTasks, setAsyncTasks] = useState<AsyncTask[]>([]);
  const [stats, setStats] = useState<ProcessingStats | null>(null);
  const [accounts, setAccounts] = useState<WhatsAppAccount[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<MessageRecord | null>(null);
  const [selectedTask, setSelectedTask] = useState<AsyncTask | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [taskDetailModalVisible, setTaskDetailModalVisible] = useState(false);
  const [messageLogs, setMessageLogs] = useState<any[]>([]);
  const [taskLogs, setTaskLogs] = useState<any[]>([]);


  // 筛选条件
  const [filters, setFilters] = useState({
    status: '',
    accountId: undefined as number | undefined,
    dateRange: null as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [accountsRes] = await Promise.all([
        whatsappConnectorService.getWhatsAppAccounts()
      ]);
      setAccounts(accountsRes.data || []);
      loadMessages();
      loadAsyncTasks();
      loadStats();
    } catch (error: any) {
      message.error(`加载数据失败: ${error.message}`);
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: 1,
        pageSize: 50
      };

      if (filters.status) params.status = filters.status;
      if (filters.accountId) params.accountId = filters.accountId;
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await whatsappConnectorService.getMessages(params);
      setMessages(response.data || []);
    } catch (error: any) {
      message.error(`加载消息列表失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAsyncTasks = async () => {
    try {
      const params: any = {
        page: 1,
        pageSize: 50
      };

      if (filters.status) params.status = filters.status;
      if (filters.accountId) params.accountId = filters.accountId;

      const response = await whatsappConnectorService.getAsyncTasks(params);
      setAsyncTasks(response.data || []);
    } catch (error: any) {
      message.error(`加载异步任务失败: ${error.message}`);
    }
  };

  const loadStats = async () => {
    try {
      const response = await whatsappConnectorService.getProcessingStats();
      setStats(response.data);
    } catch (error: any) {
      console.error('加载统计数据失败:', error);
    }
  };

  const handleViewMessageDetail = async (record: MessageRecord) => {
    try {
      const response = await whatsappConnectorService.getMessageDetail(record.id);
      setSelectedMessage(record);
      setMessageLogs(response.logs || []);
      setDetailModalVisible(true);
    } catch (error: any) {
      message.error(`获取消息详情失败: ${error.message}`);
    }
  };

  const handleViewTaskDetail = async (task: AsyncTask) => {
    try {
      const response = await whatsappConnectorService.getAsyncTaskDetail(task.id);
      setSelectedTask(task);
      setTaskLogs(response.logs || []);
      setTaskDetailModalVisible(true);
    } catch (error: any) {
      message.error(`获取任务详情失败: ${error.message}`);
    }
  };

  const handleResendMessage = async (messageId: string) => {
    try {
      await whatsappConnectorService.resendMessage(messageId);
      message.success('消息重新发送成功');
      loadMessages();
      loadStats();
    } catch (error: any) {
      message.error(`重新发送失败: ${error.message}`);
    }
  };

  const handlePushConfig = async () => {
    try {
      setLoading(true);
      const tenantCode = localStorage.getItem('currentTenant') || 'default';
      console.log('🔄 开始推送配置', { tenantCode });
      
      const result = await whatsappConnectorService.reloadConnectorConfig(tenantCode);
      console.log('✅ 配置推送成功', result);
      
      message.success('配置推送成功，远程服务缓存已刷新');
      // 刷新统计数据
      loadStats();
    } catch (error: any) {
      console.error('❌ 配置推送失败:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      message.error(`配置推送失败: ${error.message || '未知错误'}`);
    } finally {
      setLoading(false);
    }
  };


  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      pending: { color: 'default', icon: <ClockCircleOutlined />, text: '待处理' },
      processing: { color: 'processing', icon: <ReloadOutlined spin />, text: '处理中' },
      completed: { color: 'success', icon: <CheckCircleOutlined />, text: '完成' },
      failed: { color: 'error', icon: <ExclamationCircleOutlined />, text: '失败' },
      timeout: { color: 'warning', icon: <ExclamationCircleOutlined />, text: '超时' }
    };

    const config = statusMap[status] || statusMap.pending;
    return (
      <Tag color={config.color}>
        {config.icon} {config.text}
      </Tag>
    );
  };

  const getDirectionTag = (direction: string) => {
    return direction === 'inbound' ? (
      <Tag color="blue"><MessageOutlined /> 接收</Tag>
    ) : (
      <Tag color="green"><SendOutlined /> 发送</Tag>
    );
  };

  const messageColumns = [
    {
      title: '消息ID',
      dataIndex: 'whatsapp_message_id',
      key: 'whatsapp_message_id',
      width: 200,
      ellipsis: true,
    },
    {
      title: '账号',
      dataIndex: 'whatsapp_account_id',
      key: 'whatsapp_account_id',
      width: 120,
      render: (accountId: number) => {
        const account = accounts.find(acc => acc.id === accountId);
        return account ? account.account_name : `ID: ${accountId}`;
      }
    },
    {
      title: '发送者',
      dataIndex: 'from_user',
      key: 'from_user',
      width: 120,
    },
    {
      title: '接收者',
      dataIndex: 'to_user',
      key: 'to_user',
      width: 120,
    },
    {
      title: '方向',
      dataIndex: 'direction',
      key: 'direction',
      width: 80,
      render: getDirectionTag
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: '重试次数',
      dataIndex: 'retry_count',
      key: 'retry_count',
      width: 80,
    },
    {
      title: '处理时间',
      dataIndex: 'processing_time_ms',
      key: 'processing_time_ms',
      width: 100,
      render: (time: number) => time ? `${time}ms` : '-'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: MessageRecord) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewMessageDetail(record)}
            />
          </Tooltip>
          {record.status === 'failed' && (
            <Tooltip title="重新发送">
              <Popconfirm
                title="确定要重新发送这条消息吗？"
                onConfirm={() => handleResendMessage(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" icon={<SendOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const taskColumns = [
    {
      title: '任务ID',
      dataIndex: 'id',
      key: 'id',
      width: 200,
      ellipsis: true,
    },
    {
      title: '账号',
      dataIndex: 'whatsapp_account_id',
      key: 'whatsapp_account_id',
      width: 120,
      render: (accountId: number) => {
        const account = accounts.find(acc => acc.id === accountId);
        return account ? account.account_name : `ID: ${accountId}`;
      }
    },
    {
      title: 'Agent ID',
      dataIndex: 'agent_id',
      key: 'agent_id',
      width: 120,
    },
    {
      title: 'Agent请求ID',
      dataIndex: 'agent_request_id',
      key: 'agent_request_id',
      width: 150,
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: '重试次数',
      dataIndex: 'retry_count',
      key: 'retry_count',
      width: 100,
      render: (count: number, record: AsyncTask) => `${count}/${record.max_retries}`
    },
    {
      title: '下次重试',
      dataIndex: 'next_retry_at',
      key: 'next_retry_at',
      width: 160,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_: any, record: AsyncTask) => (
        <Tooltip title="查看详情">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewTaskDetail(record)}
          />
        </Tooltip>
      )
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={16} style={{ marginBottom: '20px' }}>
        <Col span={24}>
          <Card title="WhatsApp Connector 消息监控" extra={
            <Space>
              <Popconfirm
                title="配置推送"
                description="确定要推送配置到远程服务并刷新缓存吗？"
                onConfirm={handlePushConfig}
                okText="确定"
                cancelText="取消"
              >
                <Button 
                  icon={<CloudSyncOutlined />} 
                  loading={loading}
                  type="primary"
                >
                  配置推送
                </Button>
              </Popconfirm>
              <Button icon={<ReloadOutlined />} onClick={() => {
                loadMessages();
                loadAsyncTasks();
                loadStats();
              }}>
                刷新状态
              </Button>
            </Space>
          }>
            {stats && (
              <Row gutter={16}>
                <Col span={4}>
                  <Statistic
                    title="总消息数"
                    value={stats.totalMessages}
                    prefix={<MessageOutlined />}
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="已处理消息"
                    value={stats.processedMessages}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="失败消息"
                    value={stats.failedMessages}
                    prefix={<ExclamationCircleOutlined />}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="待处理异步任务"
                    value={stats.pendingAsyncTasks}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="平均处理时间"
                    value={stats.avgProcessingTime}
                    suffix="ms"
                    prefix={<BarChartOutlined />}
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="成功率"
                    value={stats.successRate}
                    suffix="%"
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: stats.successRate >= 95 ? '#3f8600' : stats.successRate >= 80 ? '#faad14' : '#cf1322' }}
                  />
                </Col>
              </Row>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: '20px' }}>
        <Col span={24}>
          <Card title="筛选条件" size="small">
            <Space wrap>
              <Select
                placeholder="选择状态"
                style={{ width: 120 }}
                allowClear
                value={filters.status || undefined}
                onChange={(value) => setFilters(prev => ({ ...prev, status: value || '' }))}
              >
                <Select.Option value="pending">待处理</Select.Option>
                <Select.Option value="processing">处理中</Select.Option>
                <Select.Option value="completed">完成</Select.Option>
                <Select.Option value="failed">失败</Select.Option>
              </Select>

              <Select
                placeholder="选择账号"
                style={{ width: 150 }}
                allowClear
                value={filters.accountId}
                onChange={(value) => setFilters(prev => ({ ...prev, accountId: value }))}
              >
                {accounts.map(account => (
                  <Select.Option key={account.id} value={account.id}>
                    {account.account_name}
                  </Select.Option>
                ))}
              </Select>

              <RangePicker
                placeholder={['开始日期', '结束日期']}
                value={filters.dateRange}
                onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null }))}
              />

              <Button type="primary" onClick={loadMessages}>
                筛选
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>


      <Tabs defaultActiveKey="messages" type="card">
        <TabPane
          tab={
            <span>
              <MessageOutlined />
              消息列表 ({messages.length})
            </span>
          }
          key="messages"
        >
          <Table
            columns={messageColumns}
            dataSource={messages}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 1200 }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
            }}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <RobotOutlined />
              异步任务 ({asyncTasks.length})
            </span>
          }
          key="asyncTasks"
        >
          <Table
            columns={taskColumns}
            dataSource={asyncTasks}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 1000 }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
            }}
          />
        </TabPane>
      </Tabs>

      {/* 消息详情模态框 */}
      <Modal
        title="消息详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
      >
        {selectedMessage && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="消息ID">{selectedMessage.whatsapp_message_id}</Descriptions.Item>
              <Descriptions.Item label="状态">{getStatusTag(selectedMessage.status)}</Descriptions.Item>
              <Descriptions.Item label="发送者">{selectedMessage.from_user}</Descriptions.Item>
              <Descriptions.Item label="接收者">{selectedMessage.to_user}</Descriptions.Item>
              <Descriptions.Item label="消息类型">{selectedMessage.message_type}</Descriptions.Item>
              <Descriptions.Item label="方向">{getDirectionTag(selectedMessage.direction)}</Descriptions.Item>
              <Descriptions.Item label="重试次数">{selectedMessage.retry_count}</Descriptions.Item>
              <Descriptions.Item label="处理时间">{selectedMessage.processing_time_ms ? `${selectedMessage.processing_time_ms}ms` : '-'}</Descriptions.Item>
              <Descriptions.Item label="创建时间" span={2}>
                {dayjs(selectedMessage.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              {selectedMessage.error_message && (
                <Descriptions.Item label="错误信息" span={2}>
                  <Alert message={selectedMessage.error_message} type="error" />
                </Descriptions.Item>
              )}
              <Descriptions.Item label="消息内容" span={2}>
                <TextArea
                  value={selectedMessage.content}
                  readOnly
                  rows={4}
                  style={{ resize: 'none' }}
                />
              </Descriptions.Item>
            </Descriptions>

            {messageLogs.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4>处理日志</h4>
                <Timeline>
                  {messageLogs.map((log, index) => (
                    <Timeline.Item key={index} color={
                      log.level === 'error' ? 'red' :
                      log.level === 'warn' ? 'orange' :
                      log.level === 'info' ? 'blue' : 'green'
                    }>
                      <div>
                        <strong>{dayjs(log.timestamp).format('HH:mm:ss')}</strong>
                        <div>{log.message}</div>
                        {log.data && (
                          <pre style={{ fontSize: '12px', marginTop: '5px' }}>
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        )}
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 异步任务详情模态框 */}
      <Modal
        title="异步任务详情"
        open={taskDetailModalVisible}
        onCancel={() => setTaskDetailModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setTaskDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
      >
        {selectedTask && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="任务ID">{selectedTask.id}</Descriptions.Item>
              <Descriptions.Item label="状态">{getStatusTag(selectedTask.status)}</Descriptions.Item>
              <Descriptions.Item label="Agent ID">{selectedTask.agent_id}</Descriptions.Item>
              <Descriptions.Item label="Agent请求ID">{selectedTask.agent_request_id}</Descriptions.Item>
              <Descriptions.Item label="重试次数">{`${selectedTask.retry_count}/${selectedTask.max_retries}`}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{dayjs(selectedTask.created_at).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
              {selectedTask.next_retry_at && (
                <Descriptions.Item label="下次重试">{dayjs(selectedTask.next_retry_at).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
              )}
              {selectedTask.response && (
                <Descriptions.Item label="响应内容" span={2}>
                  <TextArea
                    value={selectedTask.response}
                    readOnly
                    rows={6}
                    style={{ resize: 'none' }}
                  />
                </Descriptions.Item>
              )}
            </Descriptions>

            {taskLogs.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4>任务日志</h4>
                <Timeline>
                  {taskLogs.map((log, index) => (
                    <Timeline.Item key={index} color={
                      log.level === 'error' ? 'red' :
                      log.level === 'warn' ? 'orange' :
                      log.level === 'info' ? 'blue' : 'green'
                    }>
                      <div>
                        <strong>{dayjs(log.timestamp).format('HH:mm:ss')}</strong>
                        <div>{log.message}</div>
                        {log.data && (
                          <pre style={{ fontSize: '12px', marginTop: '5px' }}>
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        )}
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WhatsAppConnectorMonitorPage;
